import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { BSSettings, BSData, BSColumn, BSView, BSFilters, BSFilterItem } from './BrowseAndSelectModel';
import { FormControl } from '@angular/forms';
import { IInputProps, InputVariant, IOptionProps } from '@amobee/component-library';
import { debounceTime } from 'rxjs/operators';
import * as _ from 'lodash';

@Component({
  selector: 'browse-and-select',
  templateUrl: './browse-and-select.component.html',
  styleUrls: ['./browse-and-select.component.scss']
})
export class BrowseAndSelectComponent implements OnInit {

  @Input('selectionTableHeight') public selectionTableHeight?: string = '350px';
  @Input('selectedTableHeight') public selectedTableHeight?: string = '200px';
  @Input('selectionTableNullStateText') public selectionTableNullStateText?: string = 'No data available';
  @Input('selectedTableNullStateText') public selectedTableNullStateText?: string;
  @Input('settings') public settings: BSSettings;
  @Input('hidden') hidden: boolean = false;
  @Input('hideSelectAllCheckbox') hideSelectAllCheckbox: boolean = false;
  @Input() public set data(value: Array<BSData>) {
    this._data = [].concat(value);
    let _selectedDataId = _.map(this._selectedData, 'id');
    this._data.forEach((d: BSData) => {
      d.checked = _selectedDataId.includes(d.id);
    });
    this.updateSelectAllCheckBoxStatus();
  }
  @Input() public set preSelectedData(value: Array<BSData>) {
    let _selectedDataId = _.map(value, 'id');
    this._data.forEach((d: BSData) => {
      d.checked = _selectedDataId.includes(d.id);
    });
    this._selectedData = [].concat(value);
    this.updateSelectAllCheckBoxStatus();
  }
  @Output() public onDataChange = new EventEmitter<Array<BSData>>();
  @Output() public onFilterChange = new EventEmitter<any>();
  @Output() public onViewChange = new EventEmitter<any>();

  _data: Array<BSData> = [];
  _selectedData: Array<BSData> = [];
  toggleFilterSelection: boolean = false;
  currentSelectView: string;
  currentViewColumns: Array<BSColumn> = [];
  currentSelectedViewColumns: Array<BSColumn> = [];
  currentFilters: any = {};
  currentFilterLabel: string = '(0)';
  radioNoneValue: string = "radioNone";
  selectAllCheckboxIndeterminate: boolean = false;
  selectAllCheckboxChecked: boolean = false;
  autoCompleteSearchProps: IInputProps = {
    compact: false,
    variant: InputVariant.DEFAULT,
    placeholder: 'Enter',
    fluid: true
  };
  searchString = new FormControl();

  constructor() { }

  ngOnInit() {
    this.currentSelectView = this.settings.views[0].label;
    this.currentViewColumns = this.settings.views[0].columns;
    this.currentSelectedViewColumns = this.settings.selectedViews && this.settings.selectedViews.length ? this.settings.selectedViews[0].columns : this.currentViewColumns;
    this.searchString.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.handleOnSearchStringChanged(value);
    });
  }

  onMouseEnterSelectedRow(row: any) {
    row.showDeleteButton = true;
  }

  onMouseLeaveSelectedRow(row: any) {
    row.showDeleteButton = false;
  }

  handleOnSearchStringChanged(searchString) {
    if (searchString && searchString.length) {
      this.currentFilters.searchString = searchString;
    } else {
      this.currentFilters = _.omit(this.currentFilters, ['searchString']);
    }
    this.emitFilters();
  }

  handleOnSelectionChanged(checked: boolean, isAll?: boolean, row?: any) {
    if (isAll) {
      if (this._data.length == 0) {
        return;
      }
      this._data.forEach((d: BSData) => {
        d.checked = checked;
      });
      if (checked) {
        let selectedDataId = _.map(this._selectedData, 'id');
        this._selectedData = this._data.filter((value: BSData) => !selectedDataId.includes(value.id)).concat(this._selectedData);
      } else {
        let dataId = _.map(this._data, 'id');
        this._selectedData = this._selectedData.filter((value: BSData) => !dataId.includes(value.id));
      }
    } else {
      if (row.checked == checked) {
        // acl checkbox might have an issue that if we previous update row.checked to false and click on that checkbox it will still fire false value event
        checked = !checked;
      }
      row.checked = checked;
      if (checked) {
        this._selectedData.unshift(row);
      } else {
        let index = this._selectedData.findIndex((value: BSData) => {
          return value.id == row.id;
        });
        if (index > -1) {
          this._selectedData.splice(index, 1);
        }
      }
    }
    this.emitSelectedData();
  }

  handleOnRemoval(isAll: boolean, row?: any, indexInSelectedData?: number) {
    if (this._selectedData.length == 0) {
      return;
    }
    if (isAll) {
      this._selectedData = [];
      this._data.forEach((d: BSData) => {
        d.checked = false;
      });
    } else {
      let index = this._data.findIndex((value: BSData) => {
        return value.id == row.id;
      });
      if (index > -1) {
        this._data[index].checked = false;
      }
      this._selectedData.splice(indexInSelectedData, 1);
    }
    this.emitSelectedData();
  }

  handleOnViewChange(selectedView: IOptionProps) {
    let index = this.settings.views.findIndex((value: BSView) => {
      return value.label === selectedView.value;
    });
    if (index > -1) {
      this.currentSelectView = this.settings.views[index].label;
      this.currentViewColumns = this.settings.views[index].columns;
      this.currentSelectedViewColumns = this.settings.selectedViews && this.settings.selectedViews.length ? this.settings.selectedViews[index].columns : this.currentViewColumns;
      this.onViewChange.emit(selectedView.value);
    }
  }

  handleOnFilterChanged(filter: BSFilters, option: BSFilterItem, isChecked?: boolean) {
    if (filter.filterType === 'checkbox') {
      if (!this.currentFilters[filter.key]) {
        this.currentFilters[filter.key] = [];
      }
      if (isChecked) {
        if (!this.currentFilters[filter.key].includes(option.value)) {
          this.currentFilters[filter.key].push(option.value);
          this.emitFilters();
        }
      } else {
        let index = this.currentFilters[filter.key].indexOf(option.value);
        if (index > -1) {
          this.currentFilters[filter.key].splice(index, 1);
          if (this.currentFilters[filter.key].length == 0) {
            this.currentFilters = _.omit(this.currentFilters, [filter.key]);
          }
          this.emitFilters();
        }
      }
    } else if (filter.filterType === 'radio') {
      if (filter.currentSelected !== option.value) {
        filter.currentSelected = option.value;
        if (option.value === this.radioNoneValue) {
          this.currentFilters = _.omit(this.currentFilters, [filter.key]);
        } else {
          this.currentFilters[filter.key] = option.value;
        }
        this.emitFilters();
      }
    } else if (filter.filterType === 'autoComplete') {
      if (!filter.autoCompleteSingleSelection) {
        if (!this.currentFilters[filter.key]) {
          this.currentFilters[filter.key] = [];
        }
      }
      if (!filter.currentAutoCompleteSelection) {
        filter.currentAutoCompleteSelection = [];
      }
      if (isChecked) {
        if (filter.autoCompleteSingleSelection) {
          if (this.currentFilters[filter.key] !== option.value) {
            this.currentFilters[filter.key] = option.value;
            filter.currentSelected = option.label;
            filter.currentAutoCompleteSelection.push(option);
            this.emitFilters();
          }
        } else {
          if (!this.currentFilters[filter.key].includes(option.value)) {
            this.currentFilters[filter.key].push(option.value);
            filter.currentAutoCompleteSelection.push(option);
            filter.currentSelected = '';
            this.emitFilters();
          }
        }
      } else {
        if (!filter.autoCompleteSingleSelection) {
          let index = this.currentFilters[filter.key].indexOf(option.value);
          let chipIndex = filter.currentAutoCompleteSelection.findIndex((d: BSFilterItem) => {
            return d.value === option.value;
          });
          if (chipIndex > -1) {
            filter.currentAutoCompleteSelection.splice(chipIndex, 1);
          }
          if (index > -1) {
            this.currentFilters[filter.key].splice(index, 1);
            if (this.currentFilters[filter.key].length == 0) {
              this.currentFilters = _.omit(this.currentFilters, [filter.key]);
            }
            this.emitFilters();
          }
        }
      }
    }
  }

  handleOnClickOutside(filter: BSFilters, currentSearchString: string) {
    if (filter.autoCompleteSingleSelection && filter.currentAutoCompleteSelection && filter.currentAutoCompleteSelection.length) {
      if (!currentSearchString || currentSearchString !== filter.currentAutoCompleteSelection[0].label) {
        filter.currentSelected = '';
        filter.currentAutoCompleteSelection.length = 0;
        this.currentFilters = _.omit(this.currentFilters, [filter.key]);
        this.emitFilters();
      }
    }
  }

  updateCurrentFilterlabel() {
    let appliedFilter = _.omit(this.currentFilters, ['searchString']);
    let length = Object.keys(appliedFilter).length;
    this.currentFilterLabel = '(' + length + ')';
  }

  emitFilters() {
    this.updateCurrentFilterlabel();
    this.onFilterChange.emit(this.currentFilters);
  }

  updateSelectAllCheckBoxStatus() {
    if (this._data.length > 0) {
      if (this._selectedData.length > 0) {
        let selectedDataId = _.map(this._selectedData, 'id');
        this.selectAllCheckboxChecked = true;
        this.selectAllCheckboxIndeterminate = false;
        for (const d of this._data) {
          if (selectedDataId.includes(d.id)) {
            this.selectAllCheckboxIndeterminate = true;
          } else {
            this.selectAllCheckboxChecked = false;
          }
        }
        if (this.selectAllCheckboxChecked) {
          this.selectAllCheckboxIndeterminate = false;
        }
      } else {
        this.selectAllCheckboxChecked = false;
        this.selectAllCheckboxIndeterminate = false;
      }
    } else {
      this.selectAllCheckboxChecked = false;
      this.selectAllCheckboxIndeterminate = false;
    }
  }

  emitSelectedData() {
    this.updateSelectAllCheckBoxStatus();
    this.onDataChange.emit(this._selectedData);
  }

}
